import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { Spinner } from "reactstrap";

import { getInteractions } from "../../../../../../store/interactions/actions";
import ComponentTab from "./componentTab";
import ComponentMessage from "./componentMessage";
import MessageItem from "../../../PreviousConversations/subcomponents/MessageItem/MessageItem";
import moment from "moment-timezone";
import axios from "axios";
import "moment/locale/pt";
import "./lightTheme.scss";

import Viewer from "react-viewer";
import Button from "../../../../../Common/Button";
import { deleteCommentary } from "../../../../../../store/operator/actions";
import ConfirmModal from "../../../../../Common/ConfirmModal/ConfirmModal";
import { showToast } from "../../../../functions/utils";
import { formatDeletedAt } from "./formatDeletedAt";

const importDarktMode = (mainTheme) => {
  if (mainTheme === "dark") {
    require("./darkTheme.scss");
  }
};

function InteractionComponent({ chatId, isVisible, ...props }) {
  const { mainTheme } = useSelector((state) => state.Layout);

  importDarktMode(mainTheme);

  useEffect(() => {
    importDarktMode(mainTheme);
  }, [mainTheme]);

  const [chatImages, setChatImages] = useState();
  const [activeItem, setActiveItem] = useState(null);
  const [languageMoment, setLanguageMoment] = useState(null);
  const [conversationCommentId, setConversationCommentId] = useState("");

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const status = useSelector((state) => state.Interactions?.status?.[chatId]);
  const interactions =
    useSelector((state) => state.Interactions?.data?.[chatId]) ?? [];
  const userList = useSelector(
    (state) => state.Interactions?.userList?.[chatId]
  );

  const chats = useSelector(
    (state) => state.Operator?.messageOperator?.chats?.data
  );
  const conversationDeleteCommentStatus = useSelector(
    (state) => state.Operator.deleteCommentary
  );

  const consumer = useMemo(() => {
    const chatOfConsumer = chats?.find(
      (chat) => chat.conversationId === chatId
    );

    return chatOfConsumer?.consumer || {};
  }, [chats, chatId]);

  const interactionsFormateFields = useMemo(() => {
    let list = [];
    interactions.forEach(({ title, text, ...msg }) => {
      if (msg.type_info === "cvadfc") {
        const idxItem = list.findIndex(
          (ele) =>
            ele.type_info === "cvadfc" && ele?.created_at === msg?.created_at
        );

        if (idxItem >= 0) {
          list[idxItem]?.fields?.push({ title, text });
        } else {
          list.push({ ...msg, fields: [{ title, text }] });
        }
      } else {
        list.push({ title, text, ...msg });
      }
    });

    return list;
  }, [interactions]);

  useEffect(() => {
    setLanguageMoment(i18n.language);
  }, [t]);

  const handleRefresh = useCallback(() => {
    if (!chatId || !isVisible) return;
    dispatch(getInteractions(chatId));
  }, [chatId, isVisible, dispatch]);

  const clearConversationCommentId = useCallback(
    () => setConversationCommentId(""),
    []
  );

  const deleteConversationCommentary = useCallback(() => {
    dispatch(deleteCommentary(chatId, conversationCommentId));
    clearConversationCommentId();
  }, [chatId, dispatch, clearConversationCommentId, conversationCommentId]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  useEffect(() => {
    if (conversationDeleteCommentStatus.success) {
      showToast("success", t("Comment deleted successfully"));
      conversationDeleteCommentStatus.success = false;
    }

    if (conversationDeleteCommentStatus.error) {
      showToast("error", t("Cannot delete commentary"));
      conversationDeleteCommentStatus.error = false;
    }
  }, [conversationDeleteCommentStatus, t]);

  const getSender = (msg) => {
    if (msg?.by === "consumer" && msg?.type_info !== "cvadfc") {
      return consumer?.name || "";
    }

    if (msg?.by === "system") return t("System message");

    return userList?.find((user) => msg?.userId === user?.user_id)?.user_name;
  };

  const updateChatImages = () => {
    const aux = interactions
      ?.filter(
        (el) =>
          el?.filesConsumer &&
          el?.filesConsumer?.mime_type?.split("/")[0] === "image"
      )
      .map((msg) => ({
        src: msg?.filesConsumer?.file_url,
        fileName: msg?.filesConsumer?.file_name,
      }));

    setChatImages(aux);
  };

  useEffect(() => {
    if (!interactions?.length) return;

    updateChatImages();
  }, [interactions]);

  /**
   * Function that renders the agent and date of the tabulation executed
   * @param {Date} time Date in which the tabulation was executed
   * @param {Object} msg Message that will indicate which sender sent the message
   * @returns {JSX.Element}
   */
  const handleTimerAndName = useCallback(
    (time, msg) => {
      const userName = msg?.user_name;
      const agentName = getSender(msg);
      const newTimer = new Date(time);
      moment.locale(languageMoment);

      const formattedTimer = moment(newTimer).format("D MMMM YYYY | HH:mm:ss");

      return (
        <div className="sentInformation">
          <span>
            {userName || agentName} | {formattedTimer}
          </span>
        </div>
      );
    },
    [getSender, languageMoment]
  );

  const handleClickConversation = useCallback(async (msg) => {
    if (msg?.by !== "consumer" && !msg?.filesConsumer) return

    const chatImage = chatImages?.find((pic) => pic.src === msg?.filesConsumer?.file_url)
    const positionOfChatImage = chatImages?.indexOf(chatImage) 
    if(positionOfChatImage === -1) return setActiveItem(null)
    const token = JSON.parse(localStorage.getItem("authUser"));
    axios.post(`${process.env.REACT_APP_MESSAGES_API_URL}/log/file`, { 
      fileUrl: chatImage.src,
      fileName: chatImage.fileName,
      action: 'view'
    }, {
      headers: {
        Authorization: token.Authorization
      }
    })

    return setActiveItem(positionOfChatImage)
  }, [setActiveItem, chatImages])

  const userAgentNameThatRemovedCommentary = useCallback(
    (time, msg) => {
      const agentName = msg.deleted_by_user_name;
      const formattedTimer = formatDeletedAt(time, languageMoment);

      return (
        <div className="commentaryRemoved">
          <span>
            {t("Comment removed by {agentName} on {date}", {
              agentName,
              date: formattedTimer,
            })}
          </span>
        </div>
      );
    },
    [languageMoment, t]
  );

  return (
    <div className="chat-conversation p-3 h-100 smoothScroll">
      <ConfirmModal
        isOpen={!!conversationCommentId}
        message={t(
          "If you choose to exclude this comment from tracking, please be aware that this action cannot be reversed."
        )}
        title={t("He is sure?")}
        onCancel={clearConversationCommentId}
        onConfirm={deleteConversationCommentary}
        onClose={clearConversationCommentId}
      />
      {interactionsFormateFields?.length
        ? interactionsFormateFields?.map((msg, idx) => (
            <div
              key={idx}
              onClick={() => handleClickConversation(msg)}
            >
              {msg.by !== "consumer" &&
                msg.type_info !== "cvcom" &&
                msg?.type_info !== "cvautflow" && (
                  <ComponentTab
                    isInformationTab={msg?.is_information_tab}
                    msg={msg}
                    isRight={msg.by !== "consumer"}
                  />
                )}
              {(msg?.text || msg?.filesConsumer?.file_url) &&
                msg?.by === "consumer" &&
                msg.type_info !== "cvadfc" && (
                  <MessageItem
                    name={getSender(msg)}
                    message={msg?.text}
                    isInformationTab={msg?.is_information_tab}
                    msg={{ ...msg, chatId }}
                    file={msg?.filesConsumer}
                    time={msg?.sentTime ?? msg?.sendTime}
                    enableStarMsg
                    isFavorite={false}
                  />
                )}
              {!!(msg?.text || msg?.filesOperator?.length) &&
                msg?.by !== "consumer" &&
                msg?.type_info !== "cvautflow" && (
                  <ComponentMessage
                    isRight
                    isInformationTab={msg?.is_information_tab}
                    name={getSender(msg)}
                    message={msg?.text}
                    msg={{ ...msg, chatId }}
                    files={msg?.filesOperator}
                    time={msg?.sentTime ?? msg?.sendTime}
                    isDeleted={msg.deleted_at}
                  />
                )}
              {msg?.private_note_operator &&
                msg?.by !== "consumer" &&
                msg?.type_info !== "cvautflow" && (
                  <ComponentMessage
                    isInformationTab={msg?.is_information_tab}
                    isRight={msg.by !== "consumer"}
                    name={getSender(msg)}
                    message={msg?.private_note_operator}
                    msg={{ ...msg, chatId, private: true }}
                    files={msg?.files}
                    time={msg?.sentTime ?? msg?.sendTime}
                    hasPrivateNote
                  />
                )}

              {msg?.type_info === "cvautflow" && (
                <ComponentMessage
                  isInformationTab={msg?.is_information_tab}
                  name={getSender(msg)}
                  title={msg.title}
                  message={msg?.text}
                  msg={{ ...msg, chatId }}
                  files={msg?.filesOperator}
                  time={msg?.sentTime ?? msg?.sendTime}
                />
              )}

              {msg.type_info === "cvadfc" && (
                <ComponentTab isRight fields={msg?.fields ?? []} isField />
              )}

              {msg.by !== "consumer" &&
                handleTimerAndName(msg?.sentTime ?? msg?.sendTime, msg)}

              {msg?.text &&
                msg?.by !== "consumer" &&
                !["cvautflow", "cvtabs"].includes(msg?.type_info) &&
                !msg?.private_note_operator &&
                msg.is_public &&
                !msg.deleted_at && (
                  <div className="d-flex align-items-center justify-content-end">
                    <Button
                      variant="outlined"
                      className="w-auto mr-0 mb-4"
                      onClick={() =>
                        setConversationCommentId(msg.conversation_comment_id)
                      }
                    >
                      {t("Remove comment")}
                    </Button>
                  </div>
                )}

              {msg?.text &&
                msg?.by !== "consumer" &&
                msg?.type_info !== "cvautflow" &&
                !msg?.private_note_operator &&
                msg.deleted_at &&
                userAgentNameThatRemovedCommentary(msg.deleted_at, msg)}
            </div>
          ))
        : null}

      {!interactions?.length && !status?.loading && (
        <>
          {status?.error ? (
            <div className="information">{t(status.error)}</div>
          ) : (
            <div className="information">
              {t(
                "Oops! We were unable to return this information at this time, please try again in a few minutes."
              )}
            </div>
          )}
        </>
      )}
      {!interactions?.length && status?.loading && (
        <div className="w-100 pb-3 text-center">
          <Spinner
            animation="border"
            size="sm"
            className="spinner my-2 smallSpinner"
          />
        </div>
      )}
      <Viewer
        visible={activeItem !== null}
        onClose={() => {
          setActiveItem(null);
        }}
        images={chatImages}
        activeIndex={activeItem}
        className="customViewer"
      />
    </div>
  );
}

export default memo(InteractionComponent);
